import { BrowserModule } from "@angular/platform-browser";
import { APP_INITIALIZER, LOCALE_ID, NgModule } from "@angular/core";
import { HttpClientModule } from "@angular/common/http";
import { AppComponent } from "./app.component";
import { AppRoutingModule } from "./app-routing.module";
import { FormsModule, ReactiveFormsModule } from "@angular/forms";
import { DynamicSetDetailsComponent, DialogDataQualityReportComponent } from "./dynamic-set-details/dynamic-set-details.component";
import { PromoteDynamicComponent } from "./promote-dynamic/promote-dynamic.component";
import { PromoteStaticComponent } from "./promote-static/promote-static.component";
import { StatusDynamicComponent } from "./status/status-dynamic/status-dynamic.component";
import { StatusStaticComponent } from "./status/status-static/status-static.component";
import { StaticComponent } from "./test-static/test-static.component";
import { UploadComponent } from "./upload/upload.component";
import { LayerSetComponent } from "./dynamic/layer-set/layer-set.component";
import { LayerSetMappingComponent } from "./dynamic/layer-set-mapping/layer-set-mapping.component";
import { OptionMappingComponent } from "./dynamic/option-mapping/option-mapping.component";
import { LayersTreeComponent } from "./dynamic/layer-set/layers-tree.component";
import { FeaturesTreeComponent } from "./dynamic/option-mapping/features-tree.component";
import { ImageComponent } from "./common/image/image.component";
import { TableComponent, DialogDataUploadReportComponent } from "./common/table/table.component";
import { BrowserAnimationsModule, NoopAnimationsModule } from "@angular/platform-browser/animations";
import { MaterialModule } from "../app/material-module";
import { EnvConfigurationService } from "./services/config/env-configuration.service";
import { LogsComponent } from "./logs/logs.component";
import { DynamicImageLogsComponent } from "./admin/dynamic-image-logs/dynamic-image-logs.component";
import { MAT_DATE_LOCALE } from "@angular/material/core";
import { MatSortModule } from "@angular/material/sort";

import { MenuComponent } from "./menu/menu.component";
import { LandingPageComponent } from "./common/landing-page/landing-page.component";
import { RefreshCachesComponent } from "./admin/refresh-caches/refresh-caches.component";
import { ManageGlobalConfigsComponent, DialogDataFileContentComponent } from "./admin/manage-global-configs/manage-global-configs.component";
import { ManageAssetProfileComponent, DialogAssetFileContentComponent } from "./admin/manage-asset-profile/manage-asset-profile.component";
import { FileBrowserComponent } from "./admin/file-browser/file-browser.component";
import { FileManagerModule } from "./common/file-manager/file-manager.module";
import { BatchSummaryComponent } from "./admin/batch-summary/batch-summary.component";
import { NotifierModule, NotifierOptions } from "angular-notifier";

import { CommonModule, registerLocaleData } from "@angular/common";
import localeDe from "@angular/common/locales/de";
import {
  ManageImageSetComponent,
  DialogCopyImageSetComponent,
  DialogRenameImageSetComponent,
  DialogDeleteImageSetComponent,
} from "./admin/manage-image-set/manage-image-set.component";
import { MatToolbarModule } from "@angular/material/toolbar";
import { MatIconModule } from "@angular/material/icon";
import { MatGridListModule } from "@angular/material/grid-list";
import { MatMenuModule } from "@angular/material/menu";
import { MatCardModule } from "@angular/material/card";
import { MatDialogModule } from "@angular/material/dialog";
import { MatInputModule } from "@angular/material/input";
import { MatButtonModule } from "@angular/material/button";
import { MatTableModule } from "@angular/material/table";
import { MatPaginatorModule } from "@angular/material/paginator";
import { MatProgressSpinnerModule } from "@angular/material/progress-spinner";
import {
  DialogDataFilesContentComponent,
  ManageOneLayerLookupTablesComponent,
} from "./admin/manage-one-layer-lookup-tables/manage-one-layer-lookup-tables.component";

registerLocaleData(localeDe);

/**
 * Custom angular notifier options
 */
const customNotifierOptions: NotifierOptions = {
  position: {
    horizontal: {
      position: "right",
      distance: 12,
    },
    vertical: {
      position: "top",
      distance: 12,
      gap: 10,
    },
  },
  theme: "material",
  behaviour: {
    autoHide: false,
    onClick: false,
    onMouseover: false,
    showDismissButton: true,
    stacking: 7,
  },
  animations: {
    enabled: true,
    show: {
      preset: "slide",
      speed: 300,
      easing: "ease",
    },
    hide: {
      preset: "fade",
      speed: 300,
      easing: "ease",
      offset: 50,
    },
    shift: {
      speed: 300,
      easing: "ease",
    },
    overlap: 150,
  },
};

@NgModule({
  declarations: [
    AppComponent,
    DynamicSetDetailsComponent,
    DialogDataUploadReportComponent,
    PromoteDynamicComponent,
    PromoteStaticComponent,
    StatusDynamicComponent,
    DialogDataQualityReportComponent,
    StatusStaticComponent,
    LogsComponent,
    DynamicImageLogsComponent,
    StaticComponent,
    UploadComponent,
    LayerSetComponent,
    LayerSetMappingComponent,
    OptionMappingComponent,
    LayersTreeComponent,
    FeaturesTreeComponent,
    ImageComponent,
    TableComponent,
    MenuComponent,
    RefreshCachesComponent,
    ManageGlobalConfigsComponent,
    DialogDataFileContentComponent,
    DialogDataFilesContentComponent,
    ManageAssetProfileComponent,
    DialogAssetFileContentComponent,
    FileBrowserComponent,
    BatchSummaryComponent,
    ManageImageSetComponent,
    DialogCopyImageSetComponent,
    DialogRenameImageSetComponent,
    DialogDeleteImageSetComponent,
    LandingPageComponent,
    ManageOneLayerLookupTablesComponent,
  ],
  imports: [
    BrowserModule,
    AppRoutingModule,
    HttpClientModule,
    ReactiveFormsModule,
    NoopAnimationsModule,
    CommonModule,
    MatToolbarModule,
    MatIconModule,
    MatGridListModule,
    MatMenuModule,
    MatCardModule,
    BrowserAnimationsModule,
    MatDialogModule,
    MatInputModule,
    FormsModule,
    MatButtonModule,
    MatTableModule,
    MatSortModule,
    MaterialModule,
    FileManagerModule,
    NotifierModule.withConfig(customNotifierOptions),
    FormsModule,
    MatPaginatorModule,
    MatProgressSpinnerModule,
  ],
  providers: [
    {
      provide: APP_INITIALIZER,
      useFactory: (envConfigService: EnvConfigurationService) => () => envConfigService.load().toPromise(),
      deps: [EnvConfigurationService],
      multi: true,
    },
    {
      provide: MAT_DATE_LOCALE,
      useValue: "de-DE",
    },
    {
      provide: LOCALE_ID,
      useValue: "de-DE",
    },
  ],
  bootstrap: [AppComponent],
})
export class AppModule {}
